import type { NutritionProviderConfig, ProviderTier } from '@recipes/contracts';
import { PREMIUM_NUTRITION_ENTITLEMENT } from '@recipes/contracts';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
  Switch,
} from '@recipes/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '@/components/feedback/toaster';
import { AdminPage } from '@/features/admin/admin-layout';
import { api } from '@/lib/api';
import { describeError, toastError } from '@/lib/errors';
import { queryKeys } from '@/lib/query-keys';

function ProviderCard({ config }: { config: NutritionProviderConfig }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const ids = { enabled: useId(), priority: useId(), tier: useId(), limit: useId(), text: useId() };
  const [enabled, setEnabled] = useState(config.enabled);
  const [priority, setPriority] = useState(String(config.priority));
  const [tier, setTier] = useState<ProviderTier>(config.tier);
  const [dailyLimit, setDailyLimit] = useState(config.dailyLimit ? String(config.dailyLimit) : '');
  const [attribution, setAttribution] = useState(config.attribution ?? '');
  const usable = config.available && (!config.needsCredentials || config.hasCredentials);

  const save = useMutation({
    mutationFn: () =>
      api.admin.updateNutritionProvider(config.key, {
        enabled,
        priority: Number(priority) || 0,
        tier,
        dailyLimit: dailyLimit.trim() ? Number(dailyLimit) : null,
        attribution: attribution.trim() || null,
      }),
    onSuccess: () => {
      toast.success(t('admin.nutrition.saved', { provider: config.label }));
      void queryClient.invalidateQueries({ queryKey: queryKeys.admin.nutritionProviders });
    },
    onError: (e) => toastError(e),
  });
  const test = useMutation({
    mutationFn: () => api.admin.testNutritionProvider(config.key),
    onSuccess: (result) =>
      result.ok ? toast.success(result.message) : toast.error(result.message),
    onError: (e) => toastError(e),
  });

  return (
    <Card data-testid={`nutrition-provider-${config.key}`}>
      <CardContent className="space-y-4 p-4 md:p-6">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="font-semibold">{config.label}</h2>
          <Badge variant={config.tier === 'PREMIUM' ? 'info' : 'muted'}>
            {t(`admin.nutrition.tier.${config.tier}`)}
          </Badge>
          {!config.available && <Badge variant="warning">{t('admin.nutrition.noAdapter')}</Badge>}
          {config.needsCredentials && (
            <Badge variant={config.hasCredentials ? 'success' : 'warning'}>
              {config.hasCredentials
                ? t('admin.nutrition.credentialsSet')
                : t('admin.nutrition.credentialsMissing')}
            </Badge>
          )}
        </div>
        <div className="flex items-center justify-between gap-3">
          <Label htmlFor={ids.enabled}>{t('admin.nutrition.enabled')}</Label>
          <Switch
            id={ids.enabled}
            checked={enabled}
            disabled={!usable}
            onCheckedChange={setEnabled}
            data-testid={`nutrition-provider-${config.key}-enabled`}
          />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label htmlFor={ids.priority}>{t('admin.nutrition.priority')}</Label>
            <Input
              id={ids.priority}
              className="mt-1"
              inputMode="numeric"
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor={ids.tier}>{t('admin.nutrition.tierLabel')}</Label>
            <Select value={tier} onValueChange={(v) => setTier(v as ProviderTier)}>
              <SelectTrigger id={ids.tier} className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="FREE">{t('admin.nutrition.tier.FREE')}</SelectItem>
                <SelectItem value="PREMIUM">{t('admin.nutrition.tier.PREMIUM')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor={ids.limit}>{t('admin.nutrition.dailyLimit')}</Label>
            <Input
              id={ids.limit}
              className="mt-1"
              inputMode="numeric"
              value={dailyLimit}
              placeholder="∞"
              onChange={(e) => setDailyLimit(e.target.value)}
            />
          </div>
        </div>
        <div>
          <Label htmlFor={ids.text}>{t('admin.nutrition.attribution')}</Label>
          <Input
            id={ids.text}
            className="mt-1"
            maxLength={300}
            value={attribution}
            onChange={(e) => setAttribution(e.target.value)}
          />
        </div>
        <p className="text-xs text-muted-foreground">
          {t('admin.nutrition.requestsToday', { count: config.requestsToday })}
        </p>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => save.mutate()} loading={save.isPending}>
            {t('common.save')}
          </Button>
          <Button
            variant="outline"
            disabled={!usable}
            loading={test.isPending}
            onClick={() => test.mutate()}
          >
            {t('admin.nutrition.test')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function PremiumGrant() {
  const { t } = useTranslation();
  const emailId = useId();
  const [email, setEmail] = useState('');
  const lookup = useMutation({
    mutationFn: async (grant: boolean) => {
      const page = await api.admin.users({ q: email.trim(), limit: 5 });
      const user = page.items.find((u) => u.email.toLowerCase() === email.trim().toLowerCase());
      if (!user) throw new Error(t('admin.nutrition.userNotFound'));
      return api.admin.setEntitlements(user.id, grant ? [PREMIUM_NUTRITION_ENTITLEMENT] : []);
    },
    onSuccess: (result) =>
      toast.success(
        result.entitlements.length
          ? t('admin.nutrition.premiumGranted')
          : t('admin.nutrition.premiumRevoked'),
      ),
    onError: (e) => toastError(e),
  });
  return (
    <Card>
      <CardContent className="space-y-3 p-4 md:p-6">
        <h2 className="font-semibold">{t('admin.nutrition.premiumTitle')}</h2>
        <p className="text-sm text-muted-foreground">{t('admin.nutrition.premiumBody')}</p>
        <div className="flex flex-wrap items-end gap-2">
          <div className="min-w-0 flex-1">
            <Label htmlFor={emailId}>{t('admin.nutrition.userEmail')}</Label>
            <Input
              id={emailId}
              className="mt-1"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <Button
            disabled={!email.trim()}
            loading={lookup.isPending}
            onClick={() => lookup.mutate(true)}
          >
            {t('admin.nutrition.grant')}
          </Button>
          <Button
            variant="outline"
            disabled={!email.trim() || lookup.isPending}
            onClick={() => lookup.mutate(false)}
          >
            {t('admin.nutrition.revoke')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function BackfillCard() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const status = useQuery({
    queryKey: queryKeys.admin.catalogBackfill,
    queryFn: () => api.admin.catalogBackfill(),
    refetchInterval: (query) => (query.state.data?.state === 'RUNNING' ? 2000 : false),
  });
  const start = useMutation({
    mutationFn: () => api.admin.startCatalogBackfill(),
    onSuccess: (data) => queryClient.setQueryData(queryKeys.admin.catalogBackfill, data),
    onError: (e) => toastError(e),
  });
  const data = status.data;
  return (
    <Card>
      <CardContent className="space-y-3 p-4 md:p-6">
        <h2 className="font-semibold">{t('admin.nutrition.backfillTitle')}</h2>
        <p className="text-sm text-muted-foreground">{t('admin.nutrition.backfillBody')}</p>
        {data && (
          <p className="text-sm" data-testid="catalog-backfill-status">
            {t(`admin.nutrition.backfillState.${data.state}`)}
            {data.state !== 'IDLE' &&
              ` · ${t('admin.nutrition.backfillCounts', {
                processed: data.processed,
                linked: data.linked,
              })}`}
            {data.message ? ` · ${data.message}` : ''}
          </p>
        )}
        <Button
          onClick={() => start.mutate()}
          loading={start.isPending}
          disabled={data?.state === 'RUNNING'}
        >
          {t('admin.nutrition.backfillStart')}
        </Button>
      </CardContent>
    </Card>
  );
}

/** Nutrition data providers, the premium entitlement and the link backfill. */
export function AdminNutritionPage() {
  const { t } = useTranslation();
  const providers = useQuery({
    queryKey: queryKeys.admin.nutritionProviders,
    queryFn: () => api.admin.nutritionProviders(),
  });
  return (
    <AdminPage section="nutrition" description={t('admin.nutrition.description')}>
      {providers.isLoading && <Spinner label={t('app.loading')} />}
      {providers.error && <Alert variant="destructive">{describeError(providers.error)}</Alert>}
      <div className="grid gap-4 lg:grid-cols-2">
        {(providers.data ?? []).map((p) => (
          <ProviderCard key={p.key} config={p} />
        ))}
        <PremiumGrant />
        <BackfillCard />
      </div>
    </AdminPage>
  );
}
